<script setup lang="ts">
import type {
	PropertyPhotoCarouselDesktopEmits as Emits,
	PropertyPhotoCarouselDesktopProps as Props
} from '.';

import { isPortraitVideo } from '~/composables/useVideoUrls';
import { EventBusKeys } from '~/core/helpers/symbols';
import { getPropertyImageAlt } from '~/utils';
import emblaCarouselVue from 'embla-carousel-vue';

import { HlsVideoPlayer } from '~/components/HlsVideoPlayer';
import { StandardSlider } from '~/components/StandardSlider';
import { Skeleton } from '~/components/ui/skeleton';

import { PropertyPhotoCarouselGridItem } from './PropertyPhotoCarouselGridItem';
import { PropertyPhotoCarouselSingleItem } from './PropertyPhotoCarouselSingleItem';

interface CarouselItem {
	type: 'single' | 'grid' | 'video';
	photo?: { watermarkUrl: string; url?: string };
	photos?: Array<{ watermarkUrl: string; url?: string }>;
	videoUrl?: string;
	posterUrl?: string;
	index?: number;
	startIndex?: number;
}

interface StandardSliderExposed {
	currentCarouselItemIndex: number;
	scrollableSlidesNumber: number;
	scrollTo: (index: number, immediate?: boolean) => void;
	reinitializeCarousel: (() => void) | undefined;
}

const props = withDefaults(defineProps<Props>(), {
	isLoading: false,
	sectionName: 'hero section'
});

const emit = defineEmits<Emits>();

const standardSliderRef =
	useTemplateRef<StandardSliderExposed>('standardSliderRef');

// Determine video orientation from width/height props
// Default to vertical layout if dimensions not available (shows carousel immediately)
const shouldUseVerticalLayout = computed(() => {
	if (!props.videoUrl) return false;
	if (!props.videoWidth || !props.videoHeight) return true; // Default to vertical
	return isPortraitVideo(props.videoWidth, props.videoHeight);
});

const carouselItemSize = computed<'half-seamless'>(() => {
	// Always use 'half-seamless' for 50%+50% layout
	// Video will override to 100% via custom CSS
	return 'half-seamless';
});

// Custom Embla carousel for when video exists
const [emblaRef, emblaApi] = emblaCarouselVue({
	align: 'start',
	loop: true
});

const canScrollPrev = ref(false);
const canScrollNext = ref(false);
const emblaCurrentIndex = ref(0);

function scrollPrev() {
	emblaApi.value?.scrollPrev();
}

function scrollNext() {
	emblaApi.value?.scrollNext();
}

function updateNavigationState() {
	if (!emblaApi.value) return;
	canScrollPrev.value = emblaApi.value.canScrollPrev();
	canScrollNext.value = emblaApi.value.canScrollNext();
	emblaCurrentIndex.value = emblaApi.value.selectedScrollSnap();
}

watch(emblaApi, api => {
	if (!api) return;
	updateNavigationState();
	api.on('select', updateNavigationState);
	api.on('reInit', updateNavigationState);
});

/**
 * Original layout items (same as staging):
 * - Item 0: first photo (50%)
 * - Item 1: grid with photos 1, 2, 3 (50%)
 * - Item 2+: remaining photos individually
 */
const originalLayoutItems = computed<CarouselItem[]>(() => {
	if (!props.images || props.images.length === 0) return [];

	const items: CarouselItem[] = [];

	// First item: first photo
	if (props.images[0]) {
		items.push({
			type: 'single',
			photo: props.images[0],
			index: 0
		});
	}

	// Second item: grid with photos 1, 2, 3 (like staging)
	if (props.images.length >= 4) {
		items.push({
			type: 'grid',
			photos: props.images.slice(1, 4),
			startIndex: 1
		});
	}

	// Remaining photos individually
	const remainingStartIndex = props.images.length >= 4 ? 4 : 1;
	for (let i = remainingStartIndex; i < props.images.length; i++) {
		items.push({
			type: 'single',
			photo: props.images[i],
			index: i
		});
	}

	return items;
});

/**
 * When video is present, prepend video to original layout.
 * Otherwise, use original layout directly.
 */
const carouselItems = computed<CarouselItem[]>(() => {
	if (!props.videoUrl) {
		return originalLayoutItems.value;
	}

	// With video: video first, then original layout
	const items: CarouselItem[] = [
		{
			type: 'video',
			videoUrl: props.videoUrl,
			posterUrl: props.posterUrl,
			index: 0
		},
		...originalLayoutItems.value
	];

	return items;
});

const currentPhotoCount = computed(() => {
	if (!props.images || props.images.length === 0) return 1;

	if (props.videoUrl) {
		// With video: use embla index
		const currentIndex = emblaCurrentIndex.value;

		if (currentIndex === 0) return 1;

		// After video, items are 50% width so 2 items visible at once
		// Index 1: photo 0 (50%) + grid (50%) = 4 photos visible
		// Index 2+: grid + remaining single photos
		if (currentIndex === 1) {
			return Math.min(4, props.images.length);
		}

		// For subsequent slides
		const photosFromFirstPage = Math.min(4, props.images.length);
		const remainingPhotos = Math.max(0, props.images.length - 4);

		if (remainingPhotos === 0) return photosFromFirstPage;

		const additionalPhotos = Math.min(currentIndex - 1, remainingPhotos);
		return photosFromFirstPage + additionalPhotos;
	}

	// No video: use standard slider index (original staging logic)
	const currentIndex = standardSliderRef.value?.currentCarouselItemIndex ?? 0;

	// First carousel page shows 4 photos (1 first + 3 grid)
	if (currentIndex === 0) {
		return Math.min(4, props.images.length);
	}

	// For subsequent pages with single photos
	const photosFromFirstPage = Math.min(4, props.images.length);
	const remainingPhotos = Math.max(0, props.images.length - 4);

	if (remainingPhotos === 0) return photosFromFirstPage;

	// Each subsequent carousel item shows 1 photo
	const additionalPhotos = Math.min(currentIndex, remainingPhotos);
	return photosFromFirstPage + additionalPhotos;
});

function getImageAlt(index: number | null) {
	return getPropertyImageAlt(
		props.property?.propertyType?.name,
		props.property?.suites,
		props.property?.region,
		props.property?.city,
		props.property?.askingPrice,
		index
	);
}

function handlePhotoClick(
	photoIndex: number,
	photoUrl: string,
	clickType: 'photo' | 'cta' = 'photo'
) {
	emit('photo-clicked', {
		photoIndex,
		photoUrl,
		clickType
	});
}

const desktopCarouselCountBus = useEventBus(
	EventBusKeys.desktopHeroSectionPhotoCount
);

watch(
	currentPhotoCount,
	newValue => {
		desktopCarouselCountBus.emit(newValue);
	},
	{ immediate: true }
);
</script>

<template>
	<div class="hidden size-full xl:block">
		<!-- Vertical video: split layout (video ~30% + photos ~70%) -->
		<div
			v-if="videoUrl && shouldUseVerticalLayout"
			class="relative size-full overflow-hidden"
		>
			<div ref="emblaRef" class="embla size-full overflow-hidden">
				<div class="embla__container flex h-full gap-1">
					<!-- First slide: Video + Photos side by side -->
					<div
						class="embla__slide flex size-full shrink-0 gap-1"
						style="flex: 0 0 100%; min-width: 100%"
					>
						<!-- Video side (~30%) -->
						<div class="relative h-full w-[30%] overflow-hidden bg-black">
							<ClientOnly>
								<HlsVideoPlayer
									:video-url="videoUrl"
									:poster-url="posterUrl"
									:autoplay="true"
									:muted="true"
									:controls="true"
									:loop="true"
									class="size-full object-contain"
								/>
								<template #fallback>
									<!-- Show poster thumbnail while component hydrates -->
									<div class="size-full overflow-hidden bg-black">
										<img
											v-if="posterUrl"
											:src="posterUrl"
											alt="Video thumbnail"
											class="size-full object-cover border-0 outline-none"
											loading="eager"
											fetchpriority="high"
										/>
									</div>
								</template>
							</ClientOnly>
						</div>

						<!-- Photos side (~70%) - first photo + 2 photos column -->
						<div class="flex h-full w-[70%] gap-1">
							<!-- First photo (larger) -->
							<div
								v-if="images[0]"
								class="h-full w-1/2 cursor-pointer overflow-hidden"
								@click="handlePhotoClick(0, images[0].watermarkUrl)"
							>
								<img
									:src="images[0].watermarkUrl"
									:alt="getImageAlt(0)"
									class="size-full object-cover transition-transform duration-300 hover:scale-105"
									loading="eager"
									fetchpriority="high"
								/>
							</div>

							<!-- Grid with 2 photos -->
							<div
								v-if="images.length >= 3"
								class="flex h-full w-1/2 flex-col gap-1"
							>
								<div
									v-for="(photo, idx) in images.slice(1, 3)"
									:key="`grid-photo-${idx}`"
									class="h-1/2 cursor-pointer overflow-hidden"
									@click="handlePhotoClick(idx + 1, photo.watermarkUrl)"
								>
									<img
										:src="photo.watermarkUrl"
										:alt="getImageAlt(idx + 1)"
										class="size-full object-cover transition-transform duration-300 hover:scale-105"
										loading="eager"
										:fetchpriority="idx === 0 ? 'high' : 'auto'"
									/>
								</div>
							</div>
						</div>
					</div>

					<!-- Remaining photo slides: 50% width each -->
					<div
						v-for="(photo, index) in (images || []).slice(3)"
						:key="`item-${index + 3}`"
						class="embla__slide size-full shrink-0"
						style="flex: 0 0 50%; min-width: 50%"
					>
						<PropertyPhotoCarouselSingleItem
							:photo="photo"
							:photo-index="index + 3"
							:is-loading="isLoading"
							:alt-text="getImageAlt(index + 3)"
							:image-props="{
								preload: false,
								fetchpriority: 'auto',
								decoding: 'async'
							}"
							@photo-clicked="handlePhotoClick"
						/>
					</div>
				</div>
			</div>

			<!-- Navigation buttons -->
			<button
				v-if="canScrollPrev"
				class="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white p-2 text-black shadow-lg transition-all hover:scale-110 hover:bg-white/90"
				@click="scrollPrev"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<polyline points="15 18 9 12 15 6" />
				</svg>
			</button>

			<button
				v-if="canScrollNext"
				class="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white p-2 text-black shadow-lg transition-all hover:scale-110 hover:bg-white/90"
				@click="scrollNext"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<polyline points="9 18 15 12 9 6" />
				</svg>
			</button>
		</div>

		<!-- Horizontal video: carousel layout (video 100%, then photos) -->
		<div
			v-else-if="videoUrl && !shouldUseVerticalLayout"
			class="relative size-full overflow-hidden"
		>
			<div ref="emblaRef" class="embla size-full overflow-hidden">
				<div class="embla__container flex h-full gap-1">
					<!-- Video slide: 100% width -->
					<div
						class="embla__slide size-full shrink-0 overflow-hidden bg-black"
						:style="{ flex: '0 0 100%', minWidth: '100%' }"
					>
						<ClientOnly>
							<HlsVideoPlayer
								:video-url="videoUrl"
								:poster-url="posterUrl"
								:autoplay="true"
								:muted="true"
								:controls="true"
								:loop="true"
								class="size-full"
							/>
							<template #fallback>
								<!-- Show poster thumbnail while component hydrates - constrained to container -->
								<div class="relative size-full overflow-hidden bg-black">
									<img
										v-if="posterUrl"
										:src="posterUrl"
										alt="Video thumbnail"
										class="size-full object-cover border-0 outline-none"
										loading="eager"
										fetchpriority="high"
									/>
								</div>
							</template>
						</ClientOnly>
					</div>

					<!-- Photo slides: 50% width each (original layout) -->
					<div
						v-for="(item, index) in carouselItems.slice(1)"
						:key="`item-${index}`"
						class="embla__slide size-full shrink-0"
						:style="{ flex: '0 0 50%', minWidth: '50%' }"
					>
						<PropertyPhotoCarouselSingleItem
							v-if="item.type === 'single' && item.photo"
							:photo="item.photo"
							:photo-index="item.index ?? 0"
							:is-loading="isLoading"
							:alt-text="getImageAlt(item.index ?? 0)"
							:image-props="{
								preload: index === 0,
								fetchpriority: index === 0 ? 'high' : 'auto',
								decoding: 'async'
							}"
							@photo-clicked="handlePhotoClick"
						/>

						<PropertyPhotoCarouselGridItem
							v-if="
								item.type === 'grid' &&
								item.photos &&
								item.startIndex !== undefined
							"
							:photos="item.photos"
							:start-index="item.startIndex"
							:is-loading="isLoading"
							:get-image-alt="getImageAlt"
							:image-props="{
								preload: true,
								fetchpriority: 'high',
								decoding: 'async'
							}"
							@photo-clicked="handlePhotoClick"
						/>
					</div>
				</div>
			</div>

			<!-- Navigation buttons -->
			<button
				v-if="canScrollPrev"
				class="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white p-2 text-black shadow-lg transition-all hover:bg-white/90 hover:scale-110"
				@click="scrollPrev"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<polyline points="15 18 9 12 15 6" />
				</svg>
			</button>

			<button
				v-if="canScrollNext"
				class="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white p-2 text-black shadow-lg transition-all hover:bg-white/90 hover:scale-110"
				@click="scrollNext"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<polyline points="9 18 15 12 9 6" />
				</svg>
			</button>
		</div>

		<!-- Standard carousel without video -->
		<StandardSlider
			v-else
			ref="standardSliderRef"
			:items="carouselItems"
			:is-loading="isLoading"
			:section-name="sectionName"
			:slides-to-scroll="1"
			:carousel-options="{
				cyclic: true,
				align: 'start',
				class: 'size-full',
				carouselWrapperClasses: 'size-full'
			}"
			:show-navigation-arrows="true"
			:display-title="false"
			:display-footer="false"
			:limit-photos="false"
			carousel-content-class="lg:gap-1 h-full"
			:carousel-item-size="carouselItemSize"
			container-class="!p-0 size-full"
			main-container-class="!max-w-none size-full"
			class="size-full"
		>
			<template #items="{ item, index }">
				<div class="size-full">
					<PropertyPhotoCarouselSingleItem
						v-if="item.type === 'single' && item.photo"
						:photo="item.photo"
						:photo-index="item.index ?? 0"
						:is-loading="isLoading"
						:alt-text="getImageAlt(item.index ?? 0)"
						:image-props="{
							preload: index === 0,
							fetchpriority: index === 0 ? 'high' : 'auto',
							decoding: 'async',
							loading: index === 0 ? 'eager' : 'lazy'
						}"
						@photo-clicked="handlePhotoClick"
					/>

					<PropertyPhotoCarouselGridItem
						v-if="
							item.type === 'grid' &&
							item.photos &&
							item.startIndex !== undefined
						"
						:photos="item.photos"
						:start-index="item.startIndex"
						:is-loading="isLoading"
						:get-image-alt="getImageAlt"
						:image-props="{
							preload: false,
							decoding: 'async'
						}"
						@photo-clicked="handlePhotoClick"
					/>
				</div>
			</template>

			<template #skeleton>
				<div class="size-full space-y-1">
					<Skeleton class="size-full" variant="white" :is-active="true" />
				</div>
			</template>
		</StandardSlider>
	</div>
</template>
